import React from "react";
import { useApp } from "@/context/AppContext";
import { TouchInput } from "@/components/VirtualKeyboard";
import { Heart, X } from "lucide-react";

export function FilterBar({
  categories, activeCat, setActiveCat,
  search, setSearch,
  sizes, activeSize, setActiveSize,
  maxPrice, setMaxPrice, priceCap,
  favOnly, setFavOnly, onClear,
}) {
  const { t, lang } = useApp();

  const catName = (c) => (lang === "es" ? c.name_es : c.name_en);

  return (
    <div className="space-y-4">
      <TouchInput testId="search-input-touch" value={search} onChange={setSearch} placeholder={t("search")} />

      <div className="flex gap-2 overflow-x-auto kiosk-scroll pb-1">
        <CatPill active={!activeCat} onClick={() => setActiveCat("")} testId="category-tab-all">
          {t("all")}
        </CatPill>
        {categories.map((c) => (
          <CatPill key={c.id} active={activeCat === c.id} onClick={() => setActiveCat(c.id)} testId={`category-tab-${c.id}`}>
            {catName(c)}
          </CatPill>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          data-testid="favorites-filter-toggle"
          onClick={() => setFavOnly((v) => !v)}
          className={`min-h-[48px] px-4 rounded-xl border flex items-center gap-2 font-semibold text-sm transition-colors ${
            favOnly ? "bg-red-50 text-red-500 border-red-200 dark:bg-red-950/60 dark:text-red-400" : "bg-card hover:bg-muted"
          }`}
        >
          <Heart className={`h-4 w-4 ${favOnly ? "fill-current" : ""}`} /> {t("onlyFavorites")}
        </button>

        <div className="flex gap-1.5 overflow-x-auto kiosk-scroll">
          <SizePill active={!activeSize} onClick={() => setActiveSize("")}>{t("all")}</SizePill>
          {sizes.map((s) => (
            <SizePill key={s.id} active={activeSize === s.label} onClick={() => setActiveSize(s.label)} testId={`size-filter-${s.label}`}>
              {s.label}
            </SizePill>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-muted-foreground whitespace-nowrap">
          {t("price")}: <span className="font-mono text-foreground">${maxPrice}</span>
        </span>
        <input
          data-testid="price-range-slider"
          type="range"
          min="0"
          max={priceCap}
          value={maxPrice}
          onChange={(e) => setMaxPrice(Number(e.target.value))}
          className="flex-1 accent-[hsl(var(--primary))] h-2"
        />
        <button
          data-testid="clear-filters-button"
          onClick={onClear}
          className="h-10 px-3 rounded-lg border bg-card flex items-center gap-1 text-sm hover:bg-muted"
        >
          <X className="h-4 w-4" /> {t("clearFilters")}
        </button>
      </div>
    </div>
  );
}

function CatPill({ children, active, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`min-h-[56px] px-6 rounded-xl border font-display font-semibold whitespace-nowrap transition-colors active:scale-95 ${
        active ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"
      }`}
    >
      {children}
    </button>
  );
}

function SizePill({ children, active, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`h-10 min-w-[44px] px-3 rounded-lg border text-sm font-semibold whitespace-nowrap transition-colors ${
        active ? "bg-accent text-accent-foreground border-accent" : "bg-card hover:bg-muted"
      }`}
    >
      {children}
    </button>
  );
}
